import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MdDelete, MdAdd, MdEdit, MdPostAdd, MdShoppingCart, MdSearch, MdClose, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { gql, useSuspenseQuery, useMutation, useLazyQuery } from '@apollo/client';
import { Helmet } from 'react-helmet';

import { withScreen } from './Screen';
import Pagination from './Pagination';
import { AdList_GetAdsQuery, AdList_GetAdsQueryVariables } from '../../codegen/graphql';
import { TextInput } from './common/TextInput';
import { getTranslatedValue, Translations } from './common/TranslatedValue';
import { Button } from './common/Button';

const GET_ADS = gql`
  query AdList_GetAds($search: String, $page: Int, $pageSize: Int, $withLocalInventory: Boolean) {
    ads(search: $search, page: $page, pageSize: $pageSize, withLocalInventory: $withLocalInventory) {
      nodes {
        id
        title
        description
        mainImage {
          url
        }
      }
      total
      hasNextPage
      page
      pageSize
    }
  }
`;

// const DELETE_ADS = gql`
//   mutation DeleteAds($ids: [ID!]!) {
//     deleteAds(ids: $ids)
//   }
// `;

interface AdListProps {}


function AdList({}: AdListProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get params directly from URL
  const page = Number(searchParams.get('page') || '1');
  const search = searchParams.get('search') || '';
  const withLocalInventoryParam = searchParams.get('withLocalInventory');
  const withLocalInventory = withLocalInventoryParam === 'true'
    ? true
    : withLocalInventoryParam === 'false'
      ? false
      : undefined;
  
  const [selectedAds, setSelectedAds] = useState<Set<string>>(new Set());
  const [searchInputValue, setSearchInputValue] = useState(search);
  const [pageSize] = useState(50);
  
  // Use URL params directly in the query - don't use state
  const { data } = useSuspenseQuery<AdList_GetAdsQuery, AdList_GetAdsQueryVariables>(
    GET_ADS,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        search,
        page,
        pageSize,
        withLocalInventory
      }
    }
  );
  
  // Update search input when URL search param changes
  useEffect(() => {
    setSearchInputValue(search);
  }, [search]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
  };

  const handleSearchSubmit = () => {
    const params = new URLSearchParams(searchParams);
    if (searchInputValue) {
      params.set('search', searchInputValue);
    } else {
      params.delete('search');
    }
    params.delete('page'); // Reset to page 1 when searching
    setSearchParams(params);
  };

  // Debounce search input to avoid too many requests
  React.useEffect(() => {
    if (searchInputValue === search) return; // Don't update if value hasn't changed from URL
    
    const timeoutId = setTimeout(() => {
      handleSearchSubmit();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInputValue]);

  const clearSearch = () => {
    setSearchInputValue('');
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    params.delete('page');
    setSearchParams(params);
  };

  const toggleSelection = (id: string) => {
    const newSelection = new Set(selectedAds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedAds(newSelection);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    if (newPage > 1) {
      params.set('page', newPage.toString());
    } else {
      params.delete('page');
    }
    setSearchParams(params);
  };

  const handleLocalInventoryFilterChange = (value: '' | 'true' | 'false') => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('withLocalInventory', value);
    } else {
      params.delete('withLocalInventory');
    }
    params.delete('page');
    setSearchParams(params);
  };

  // Calculate total pages
  const totalPages = Math.ceil(data.ads.total / data.ads.pageSize);

  return (
    <>
      <Helmet>
        <title>Ads</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">        
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="md:col-span-2">
            <TextInput
              LeftAccessory={<MdSearch className="h-5 w-5 text-gray-400" />}
              placeholder="Search by title, supplier product ID, or SKU..."
              value={searchInputValue}
              onChange={handleSearchChange}
            />
          </div>
          <select
            value={withLocalInventoryParam === 'true' || withLocalInventoryParam === 'false' ? withLocalInventoryParam : ''}
            onChange={(e) => handleLocalInventoryFilterChange(e.target.value as '' | 'true' | 'false')}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All inventory</option>
            <option value="true">With local inventory</option>
            <option value="false">Without local inventory</option>
          </select>
        </div>
        
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Ads ({data.ads.total})</h1>
          <div className="flex gap-4">
            <Button
              onClick={() => navigate('/ads/new')}
              preset="blue"
            >
              <MdAdd size={20} />
              New Ad
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <Pagination
            currentPage={data.ads.page}
            totalPages={totalPages}
            totalItems={data.ads.total}
            pageSize={data.ads.pageSize}
            hasNextPage={data.ads.hasNextPage}
            onPageChange={handlePageChange}
          />

          {/* Grid Header */}
          <div className="grid grid-cols-12 gap-4 bg-gray-50 px-6 py-3">
            <div className="col-span-1">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedAds(new Set(data.ads.nodes.map(ad => ad.id)));
                  } else {
                    setSelectedAds(new Set());
                  }
                }}
                checked={selectedAds.size === data.ads.nodes.length && data.ads.nodes.length > 0}
              />
            </div>
            <div className="col-span-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Image
            </div>
            <div className="col-span-10 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Title
            </div>
          </div>

          {/* Grid Content */}
          <div className="divide-y divide-gray-200">
            {data.ads.nodes.length === 0 ? (
              <div className="px-6 py-4 text-center text-gray-500">
                {search ? 'No ads match your search criteria' : 'No ads found'}
              </div>
            ) : (
              data.ads.nodes.map((ad) => (
                <React.Fragment key={ad.id}>
                  <div className="relative">
                    <a
                      href={`/ad/${ad.id}/edit`}
                      onClick={(e) => {
                        if (!e.ctrlKey) {
                          e.preventDefault();
                          navigate(`/ad/${ad.id}/edit`);
                        }
                      }}
                      className="block grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="col-span-1" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={selectedAds.has(ad.id)}
                          onChange={() => toggleSelection(ad.id)}
                        />
                      </div>
                      <div className="col-span-1">
                        <img
                          src={`/img/w_80,h_80/${ad.mainImage?.url}`}
                          alt={getTranslatedValue(ad.title as unknown as Translations, 'FR')}
                          className=""
                        />
                      </div>
                      <div className="col-span-10">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-900">
                            {getTranslatedValue(ad.title as unknown as Translations, 'FR')}
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                </React.Fragment>
              ))
            )}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={data.ads.page}
            totalPages={totalPages}
            totalItems={data.ads.total}
            pageSize={data.ads.pageSize}
            hasNextPage={data.ads.hasNextPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </>
  );
}

export default withScreen(AdList);